import { classNames } from "../helpers";
import React, { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { CaretDown, Plus } from "@phosphor-icons/react";

interface CheckoutAccordionProps {
    id: string;
    titleIcon?: JSX.Element;
    titleText?: React.ReactNode;
    subTitleText?: React.ReactNode;
    content: React.ReactNode;
    defaultOpen?: boolean;
    callbackOnToggle?: (isOpen: boolean) => void;
    customClass?: string;
    buttonClass?: string;
    insideClass?: string;
    loading?: boolean;
    renderAccordionIcon?: (active: boolean) => JSX.Element | null;
    renderTitleText?: (active: boolean) => React.ReactNode;
    renderTitleIcon?: (active: boolean) => JSX.Element;
}

export interface CheckoutAccordionRefType {
    openAccordion: () => void;
    closeAccordion: () => void;
    scrollIntoView: () => void;
}

export const CheckoutAccordion = forwardRef<CheckoutAccordionRefType, CheckoutAccordionProps>(
    (
        {
            id,
            titleIcon,
            titleText,
            content,
            defaultOpen = false,
            subTitleText,
            callbackOnToggle,
            customClass,
            buttonClass,
            insideClass,
            loading = false,
            renderAccordionIcon,
            renderTitleText,
            renderTitleIcon,
        },
        ref,
    ) => {
        const [active, setActive] = useState(defaultOpen);

        useEffect(() => {
            if (!defaultOpen && active) {
                setActive(false);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [defaultOpen]);

        function toggleAccordion() {
            setActive((prevState) => !prevState);
            if (callbackOnToggle) {
                callbackOnToggle(!active);
            }
        }

        useImperativeHandle(ref, () => ({
            openAccordion() {
                setActive(true);
            },

            closeAccordion() {
                setActive(false);
            },

            scrollIntoView() {
                const section = document.querySelector(`#${id}`);
                if (section) {
                    section.scrollIntoView({ behavior: "smooth", block: "center" });
                }
            },
        }));

        const getAccordionIcon = () => {
            // If callback is provided, use it to render the icon based on active state
            if (renderAccordionIcon) {
                return renderAccordionIcon(active);
            }

            // Default icon with rotation
            return (
                <CaretDown
                    weight="bold"
                    className={classNames(
                        `h-4 w-4 text-coal-dark `,
                        active
                            ? `rotate-180 transform duration-700 ease-in-out`
                            : `rotate-0 transform duration-700 ease-in-out`,
                    )}
                />
            );
        };

        const renderContent = () => {
            const displayTitleText = renderTitleText ? renderTitleText(active) : titleText;
            const displayTitleIcon = renderTitleIcon ? renderTitleIcon(active) : titleIcon;

            return (
                <div className={`flex w-full flex-row items-center justify-between ${insideClass}`}>
                    {Boolean(displayTitleText) && (
                        <div className="flex flex-row items-center space-x-1.5">
                            {displayTitleIcon}
                            <p className="text-sm font-medium text-coal-dark">{displayTitleText}</p>
                        </div>
                    )}
                    <div className="flex flex-row items-center gap-2">
                        {Boolean(subTitleText) && <p className="text-sm text-coal-light">{subTitleText}</p>}

                        {getAccordionIcon()}
                    </div>
                </div>
            );
        };

        return (
            <div
                id={id}
                className={`mx-3 mt-3 flex flex-col
                    } ${customClass}`}>
                <div className={loading ? "opacity-50 pointer-events-none" : ""}>
                    <button
                        className={classNames(
                            `flex h-[3.25rem] w-full cursor-pointer flex-row items-center rounded-[inherit] px-3 py-4 focus:outline-none`,
                            buttonClass ?? "",
                        )}
                        onClick={toggleAccordion}>
                        {renderContent()}
                    </button>
                </div>
                <div
                    className={`overflow-hidden transition-[max-height] ${!active && "hidden"} ${active ? `duration-700 ease-in` : `duration-500 ease-out`
                        } ${active ? `max-h-[9999px]` : ` max-h-0`} `}>
                    {content}
                </div>
            </div>
        );
    },
);

CheckoutAccordion.displayName = "CheckoutAccordian";


